// src/pages/NotificationManagement/NotificationManagement.jsx
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserFriends,
  faStore,
  faUserTie
} from '@fortawesome/free-solid-svg-icons';
import { usePermissions } from '../../context/PermissionContext';
import BackButton from '../../components/BackButton';

// Define the Notification Management options
const notificationOptions = [
  {
    title: 'Customer Notification',
    icon: faUserFriends,
    path: '/notification/customer-notification',
    perm: 'notifications.customer',
    description: 'Send and manage notifications to customers'
  },
  {
    title: 'Merchant Notification',
    icon: faStore,
    path: '/notification/shop-notification',
    perm: 'notifications.shop',
    description: 'Send and manage notifications to merchants'
  },
  {
    title: 'Agent Notification',
    icon: faUserTie,
    path: '/notification/agent-notification',
    perm: 'notifications.agent',
    description: 'Send location based notifications to agents (Level 5)'
  }
];

const NotificationManagement = () => {
  const { can, loading } = usePermissions();
  const navigate = useNavigate();

  const handleCardClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  // Check if user has access to any of the cards
  const hasAnyAccess = notificationOptions.some(option => can(option.perm));

  if (!hasAnyAccess) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography variant="h6" color="error">
          You don't have permission to access notification features. Please contact your administrator.
        </Typography>
      </Grid>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br/><br/>
      <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 4 ,mt:-7}}>
        Notification Management
      </Typography>
      
      <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ padding: 2 }}>
        {notificationOptions.filter((option) => can(option.perm)).map((option) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={option.title}>
            <Card
              elevation={3}
              sx={{
                maxWidth: 225,
                height: '100%',
                backgroundColor: 'white',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                  '& .MuiTypography-root': { color: 'white' },
                  '& .fa-icon': { color: 'white' },
                },
              }}
            >
              <CardActionArea 
                onClick={() => handleCardClick(option.path)}
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <CardContent sx={{ textAlign: 'center', padding: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <FontAwesomeIcon
                    icon={option.icon}
                    size="5x"
                    className="fa-icon"
                    style={{ color: 'orange', marginBottom: '16px' }}
                  />
                  <Typography 
                    gutterBottom 
                    variant="h5" 
                    component="div" 
                    sx={{ fontWeight: 500 }}
                  >
                    {option.title}
                  </Typography>
                  <Typography 
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, fontSize: '0.85rem' }}
                  >
                    {option.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default NotificationManagement;